import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { DropEvent, FileRejection } from 'react-dropzone';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';

import useAuth from '../../hooks/useAuth';
import { useEntity } from '../../context/EntityContext';
import {
  createPublication,
  getJournalConfiguration,
  JournalConfiguration,
} from '../../services/publicationsService';
import FileUploader from './FileUploader';
import AnimatedBackdrop from '../../components/AnimatedBackdrop';
import SendIcon from '@mui/icons-material/Send';

const CreatePublication: React.FC = () => {
  const navigate = useNavigate();
  const { selectedEntity } = useEntity();
  const { user } = useAuth();

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const [isForDom, setIsForDom] = useState<boolean>(true);
  const [isForDou, setIsForDou] = useState<boolean>(false);
  const [isForJgc, setIsForJgc] = useState<boolean>(false);

  // Controla carregamento do form e do "processamento"
  const [loading, setLoading] = useState<boolean>(false);
  const [showProcessingBackdrop, setShowProcessingBackdrop] = useState<boolean>(false);

  const [journalConfig, setJournalConfig] = useState<JournalConfiguration | null>(null);
  const [configLoading, setConfigLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedEntity) return;

    setConfigLoading(true);
    getJournalConfiguration(selectedEntity)
      .then((cfg: JournalConfiguration) => {
        setJournalConfig(cfg);

        // Se só DOM estiver ativo, força isForDom = true
        // e define as outras como false
        if (cfg.dom_enabled && !cfg.dou_enabled && !cfg.jgc_enabled) {
          setIsForDom(true);
          setIsForDou(false);
          setIsForJgc(false);
        }
      })
      .catch((err: unknown) => {
        console.error('Erro ao carregar configurações da entidade:', err);
        setJournalConfig(null);
      })
      .finally(() => {
        setConfigLoading(false);
      });
  }, [selectedEntity]);

  const handleDrop = (accepted: File[], rejected: FileRejection[], _event: DropEvent) => {
    setAcceptedFiles((prev) => [...prev, ...accepted]);
    setRejectedFiles((prev) => [...prev, ...rejected]);
  };

  const handleClearAll = () => {
    setAcceptedFiles([]);
    setRejectedFiles([]);
  };

  const handleRemoveFile = (file: File) => {
    setAcceptedFiles((prev) => prev.filter((f) => f !== file));
  };

  const handleRemoveRejectedFile = (fileWithPath: File) => {
    setRejectedFiles((prev) => prev.filter((fr) => fr.file !== fileWithPath));
  };

  const handleSubmit = async () => {
    if (!selectedEntity) {
      swal('Atenção', 'Selecione uma entidade para prosseguir.', 'info');
      return;
    }
    if (acceptedFiles.length === 0) {
      swal('Atenção', 'Nenhum arquivo PDF selecionado.', 'info');
      return;
    }

    setLoading(true);
    setShowProcessingBackdrop(true);

    try {
      const payload = {
        entity_id: selectedEntity,
        is_for_dom: isForDom,
        is_for_dou: isForDou,
        is_for_jgc: isForJgc,
        files: acceptedFiles,
      };
      const result = await createPublication(payload);

      // Observamos que "result" deve conter "categories", "is_for_dom",
      // "is_for_dou", "is_for_jgc" etc.
      console.log('Publicação criada:', result);

      setTimeout(() => {
        navigate('/publicacoes/resultado', {
          state: {
            publication: {
              publication_id: result.publication_id,
              pdf_path: result.pdf_path,
              title: result.title,
              resume: result.resume,
              table_of_contents: result.table_of_contents,
              categories: result.categories, // agora passamos também as categorias
              is_for_dom: result.is_for_dom,
              is_for_dou: result.is_for_dou,
              is_for_jgc: result.is_for_jgc,
            },
          },
        });
      }, 800);

    } catch (error: any) {
      console.error('Erro ao criar publicação:', error);
      swal('Erro', 'Falha ao criar a publicação. Tente novamente.', 'error');
    } finally {
      setLoading(false);
      setShowProcessingBackdrop(false);
    }
  };

  if (!selectedEntity) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="body1" color="error">
          Selecione uma entidade antes de prosseguir.
        </Typography>
      </Box>
    );
  }

  if (configLoading) {
    return (
      <Box sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const onlyDomActive =
    journalConfig?.dom_enabled &&
    !journalConfig?.dou_enabled &&
    !journalConfig?.jgc_enabled;

  return (
    <>
      {showProcessingBackdrop && (
        <AnimatedBackdrop
          title="Analisando documentos"
          subtitle="Aguarde alguns instantes enquanto eu verifico o conteúdo dos documentos e extraio as informações importantes."
        />
      )}

      <Box
        sx={{
          mt: 4,
          opacity: showProcessingBackdrop ? 0.3 : 1,
          pointerEvents: showProcessingBackdrop ? 'none' : 'auto'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5">Criar Nova Publicação</Typography>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {!onlyDomActive && journalConfig?.dom_enabled && (
            <FormControlLabel
              label="Diário Oficial da Entidade (próprio)"
              control={
                <Checkbox
                  checked={isForDom}
                  onChange={(e) => setIsForDom(e.target.checked)}
                />
              }
            />
          )}

          {journalConfig?.dou_enabled && (
            <FormControlLabel
              label="Diário Oficial da União"
              control={
                <Checkbox
                  checked={isForDou}
                  onChange={(e) => setIsForDou(e.target.checked)}
                />
              }
            />
          )}

          {journalConfig?.jgc_enabled && (
            <FormControlLabel
              label="Jornal de Grande Circulação"
              control={
                <Checkbox
                  checked={isForJgc}
                  onChange={(e) => setIsForJgc(e.target.checked)}
                />
              }
            />
          )}
        </Box>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Arquivos (somente PDFs):
        </Typography>
        <FileUploader
          onDrop={handleDrop}
          acceptedFiles={acceptedFiles}
          rejectedFiles={rejectedFiles}
          handleClearAll={handleClearAll}
          handleRemoveFile={handleRemoveFile}
          handleRemoveRejectedFile={handleRemoveRejectedFile}
        />

        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleSubmit}
          startIcon={!loading ? <SendIcon /> : undefined}
          sx={{ mt: 3, mb: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : (
            'Enviar'
          )}
        </Button>
      </Box>
    </>
  );
};

export default CreatePublication;